/**
 * Account Settings Page
 *
 * Comprehensive account management interface for all user types.
 * Provides settings for profile, email preferences, password, and account deletion.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-151 - Shared Account Settings (13 SP)
 *
 * Features:
 * - Profile information editing (name, phone)
 * - Email notification preferences
 * - Password change with verification
 * - Account deletion with password confirmation
 *
 * Authorization:
 * - All sections require authentication via @RequireAuth
 * - Users can only manage their own account
 *
 * @version 1.0
 * @created 2026-01-12
 */

import React, { useState, useEffect } from 'react';
import apiRequest from '../../services/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  User as _User,
  Mail as _Mail,
  Lock as _Lock,
  AlertTriangle,
  Save as _Save,
  Bell,
  Settings,
  Monitor as _Monitor,
  Shield
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import TabGroup from '../../components/common/TabGroup';
import DashboardLayout from '../../components/layout/DashboardLayout';
import ProfileSettings from '../../components/account/ProfileSettings';
import EmailPreferences from '../../components/account/EmailPreferences';
import CustomerPreferences from '../../components/account/CustomerPreferences';
import AccountClosure from '../../components/account/AccountClosure';
import NotificationPreferences from '../../components/notifications/NotificationPreferences';
import PrivacySettings from '../../components/account/PrivacySettings';
import IntegrationsTab from '../../components/account/IntegrationsTab';
import AccountSecurity from '../../components/account/AccountSecurity';

/**
 * Main Account Settings Page Component
 */
export default function AccountSettings() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Tab state — initialised from ?tab= URL param if present
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    return tabParam || 'profile';
  });

  // Account data state (must be declared before use)
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get user role from account data
  const userRole = accountData?.user_type || accountData?.role || 'customer';

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  // Fetch account settings on mount
  useEffect(() => {
    fetchAccountSettings();
  }, []);

  /**
   * Fetch account settings from API
   */
  const fetchAccountSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiRequest('/api/v1/account/settings', { method: 'GET' });

      if (response.success) {
        setAccountData(response.data);
      }
    } catch (err) {
      console.error('Error fetching account settings:', err);
      if (err.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to load account settings');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle successful profile update
   */
  const handleProfileUpdate = (updatedData) => {
    setAccountData(prev => ({
      ...prev,
      ...updatedData
    }));
  };

  /**
   * Handle successful email preferences update
   */
  const handleEmailPreferencesUpdate = (updatedPreferences) => {
    setAccountData(prev => ({
      ...prev,
      email_preferences: updatedPreferences
    }));
  };

  /**
   * Handle successful account deletion
   */
  const handleAccountDeleted = () => {
    // Clear auth token and redirect to home
    localStorage.removeItem('token');
    navigate('/');
  };

  // Tab configuration based on user role
  const tabs = [
    { id: 'profile', label: 'Profile', icon: _User },
    ...(userRole === 'customer'
      ? [{ id: 'preferences', label: 'Preferences', icon: Settings }]
      : [{ id: 'email', label: 'Email Preferences', icon: _Mail }]
    ),
    // SCRUM-2262: Account Security tab consolidates password mgmt,
    // connected sign-in methods, lock-out modal, and (for guides)
    // MFA + active sessions sections.
    { id: 'security', label: 'Security', icon: Shield },
    // Guide-specific tabs (SCRUM-528: Privacy Settings & Integrations)
    ...(userRole === 'guide'
      ? [
          { id: 'privacy', label: 'Privacy Settings', icon: Shield },
          { id: 'integrations', label: 'Integrations', icon: Settings }
        ]
      : []
    ),
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'danger', label: 'Delete account', icon: AlertTriangle },
  ];

  // Render loading state
  if (loading) {
    return (
      <DashboardLayout
        userRole={userRole}
        activeNavId="profile"
        onNavigate={handleNavigate}
        user={{ name: 'User', email: 'user@outdoorguides.com', avatar: null }}
      >
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Account Settings</h1>
          <LoadingSpinner message="Loading account settings..." size="md" color="border-brand-primary" className="min-h-[400px]" />
        </div>
      </DashboardLayout>
    );
  }

  // Render error state
  if (error) {
    return (
      <DashboardLayout
        userRole={userRole}
        activeNavId="profile"
        onNavigate={handleNavigate}
        user={{ name: 'User', email: 'user@outdoorguides.com', avatar: null }}
      >
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Account Settings</h1>
          <Alert variant="error" className="p-6 text-center">
            <div>
              <h3 className="text-lg font-semibold text-red-900 mb-2">Failed to Load Settings</h3>
              <p className="text-sm text-red-700 mb-4">{error}</p>
              <button
                onClick={fetchAccountSettings}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </Alert>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      userRole={userRole}
      activeNavId="profile"
      onNavigate={handleNavigate}
      user={{ name: 'User', email: 'user@outdoorguides.com', avatar: null }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Account Settings</h1>
          <p className="text-sm text-gray-600">
            Manage your account information, preferences, and security settings.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <TabGroup
            tabs={tabs.map(({ id, label }) => ({ id, label }))}
            activeTab={activeTab}
            onChange={setActiveTab}
          />

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'profile' && (
              <ProfileSettings
                accountData={accountData}
                onUpdate={handleProfileUpdate}
              />
            )}

            {activeTab === 'email' && userRole !== 'customer' && (
              <EmailPreferences
                emailPreferences={accountData?.email_preferences}
                onUpdate={handleEmailPreferencesUpdate}
              />
            )}

            {activeTab === 'preferences' && userRole === 'customer' && (
              <CustomerPreferences
                preferences={accountData?.customer_preferences}
                onUpdate={(updatedPreferences) => {
                  setAccountData(prev => ({
                    ...prev,
                    customer_preferences: updatedPreferences
                  }));
                }}
              />
            )}

            {activeTab === 'security' && (
              <AccountSecurity accountData={accountData} />
            )}

            {activeTab === 'privacy' && userRole === 'guide' && (
              <PrivacySettings />
            )}

            {activeTab === 'integrations' && userRole === 'guide' && (
              <IntegrationsTab />
            )}

            {activeTab === 'notifications' && (
              <NotificationPreferences />
            )}

            {activeTab === 'danger' && (
              <AccountClosure
                onAccountDeleted={handleAccountDeleted}
              />
            )}
          </div>
        </div>

        {/* Help Text */}
        <Alert variant="info" className="p-4">
          <p className="text-sm">
            <strong>Need help?</strong> Contact our support team if you have questions about your account settings.
          </p>
        </Alert>
      </div>
    </DashboardLayout>
  );
}
